import {
  ATTACHMENT_KINDS,
  BLANK_FORMS,
  EMPLOYMENT_TYPES,
  GENDER_OPTIONS,
  INSURANCE_STATUSES,
  MARITAL_OPTIONS,
  assessInsuranceFile,
  buildDraftFromEmployee,
  deadlineLabel,
  describeDeadline,
  emptyPeriod,
} from "../../utils/socialInsurance";

function labelOf(options, value) {
  return options.find((item) => item.value === value)?.label || value || "—";
}

function Sheet({ title, code, children }) {
  return (
    <article className="si-print-sheet">
      <header className="si-print-head">
        <div>
          <strong>الهيئة القومية للتأمين الاجتماعي</strong>
          <span>جمهورية مصر العربية</span>
        </div>
        <div className="si-print-code">{code}</div>
      </header>
      <h2>{title}</h2>
      {children}
      <p className="si-print-note">
        يُقدَّم نموذج رقم 1 من أصل وصورتين، مستوفي التوقيعات، إلى مكتب التأمينات
        خلال أسبوعين من تاريخ الالتحاق.
      </p>
    </article>
  );
}

function Row({ label, value }) {
  return (
    <div className="si-print-row">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

export default function SocialInsurancePrint({ draft, employee }) {
  if (!draft) return null;
  const form1 = draft.form1 || {};
  const form3 = draft.form3 || {};
  const form4 = draft.form4 || {};
  const form5 = draft.form5 || {};
  const periods = Array.isArray(form3.periods) ? form3.periods : [];

  return (
    <div className="si-print-root">
      <Sheet title="نموذج رقم (1) طلب اشتراك مؤمن عليه" code="س 1">
        <h3>بيانات صاحب العمل</h3>
        <Row label="اسم المنشأة" value={form1.establishmentName} />
        <Row label="رقم المنشأة" value={form1.establishmentNumber} />
        <Row label="العنوان" value={form1.establishmentAddress} />
        <Row label="المحافظة" value={form1.governorate} />
        <Row label="النشاط" value={form1.activity} />
        <Row label="مكتب التأمينات" value={form1.insuranceOffice} />
        <h3>بيانات المؤمن عليه</h3>
        <Row label="الاسم" value={form1.fullName} />
        <Row label="الرقم القومي" value={form1.nationalId} />
        <Row label="تاريخ الميلاد" value={form1.birthDate} />
        <Row label="محل الميلاد" value={form1.birthPlace} />
        <Row label="النوع" value={labelOf(GENDER_OPTIONS, form1.gender)} />
        <Row label="الجنسية" value={form1.nationality} />
        <Row
          label="الحالة الاجتماعية"
          value={labelOf(MARITAL_OPTIONS, form1.maritalStatus)}
        />
        <Row label="العنوان" value={form1.address} />
        <Row label="الهاتف" value={form1.phone} />
        <Row label="المؤهل" value={form1.qualification} />
        <Row label="المهنة" value={form1.jobTitle} />
        <Row label="تاريخ الالتحاق" value={form1.hireDate} />
        <Row label="تاريخ بدء الاشتراك" value={form1.subscriptionStartDate} />
        <Row
          label="نوع مدة العمل"
          value={labelOf(EMPLOYMENT_TYPES, form1.employmentType)}
        />
        <Row label="الأجر الأساسي" value={form1.basicWage} />
        <Row label="الأجر المتغير" value={form1.variableWage} />
        <Row label="الأجر الشامل" value={form1.totalWage} />
        <Row
          label="مدد اشتراك سابقة"
          value={form1.hasPreviousPeriods ? "نعم" : "لا"}
        />
        <Row label="كود الموظف" value={employee?.employeeCode} />
        <div className="si-print-signs">
          <div>
            <span>توقيع المؤمن عليه</span>
            <strong>{form1.insuredSigned ? "تم على الأصل" : ".............."}</strong>
          </div>
          <div>
            <span>صاحب العمل أو المفوض: {form1.authorizedPersonName || "—"}</span>
            <strong>{form1.employerSigned ? "تم التوقيع" : ".............."}</strong>
          </div>
          <div>
            <span>ختم المنشأة</span>
            <strong>{form1.stamped ? "تم الختم" : ".............."}</strong>
          </div>
        </div>
      </Sheet>

      <Sheet title="نموذج رقم (3) صحيفة البيانات الأساسية" code="نموذج 3">
        <Row label="الاسم" value={form1.fullName} />
        <Row label="الرقم القومي" value={form1.nationalId} />
        <Row label="الرقم التأميني" value={form3.insuranceNumber || draft.insuranceNumber} />
        {periods.length === 0 ? (
          <p>لا توجد مدد اشتراك سابقة.</p>
        ) : (
          <table className="si-print-table">
            <thead>
              <tr>
                <th>جهة العمل</th>
                <th>رقم المنشأة</th>
                <th>من</th>
                <th>إلى</th>
                <th>الأجر</th>
                <th>سبب الانتهاء</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((row, index) => (
                <tr key={`${row.employerName}-${index}`}>
                  <td>{row.employerName}</td>
                  <td>{row.establishmentNumber}</td>
                  <td>{row.from}</td>
                  <td>{row.to}</td>
                  <td>{row.wage}</td>
                  <td>{row.endReason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Sheet>

      <Sheet title="نموذج رقم (4) بيان المدد السابقة أو معاش آخر" code="نموذج 4">
        <Row
          label="مدد اشتراك سابقة"
          value={form1.hasPreviousPeriods || form4.hasPreviousPeriods ? "نعم" : "لا"}
        />
        <Row
          label="يستحق معاشاً آخر"
          value={form4.hasOtherPension ? "نعم" : "لا"}
        />
        <Row label="نوع المعاش" value={form4.otherPensionType} />
        <Row label="جهة الصرف" value={form4.otherPensionSource} />
        <Row label="تاريخ الاستحقاق" value={form4.otherPensionStartDate} />
        <p>{form4.declaration}</p>
      </Sheet>

      <Sheet title="نموذج رقم (5) بيانات التغطية التأمينية" code="نموذج 5">
        <Row label="الشيخوخة والعجز والوفاة" value={form5.oldAge ? `نعم — ${form5.oldAgeStart || ""}` : "لا"} />
        <Row label="إصابات العمل" value={form5.workInjury ? `نعم — ${form5.workInjuryStart || ""}` : "لا"} />
        <Row label="المرض" value={form5.sickness ? `نعم — ${form5.sicknessStart || ""}` : "لا"} />
        <Row label="البطالة" value={form5.unemployment ? `نعم — ${form5.unemploymentStart || ""}` : "لا"} />
        <Row label="الأجر الأساسي" value={form5.basicWage || form1.basicWage} />
        <Row label="الأجر المتغير" value={form5.variableWage || form1.variableWage} />
        <Row label="تاريخ بدء التغطية" value={form5.subscriptionStartDate || form1.subscriptionStartDate} />
      </Sheet>
      <p className="si-print-meta">
        {INSURANCE_STATUSES[draft.status] || draft.status} —{" "}
        {deadlineLabel(
          describeDeadline(form1.hireDate || employee?.hireDate, draft.status),
        )}
      </p>
      <ul className="si-print-files">
        {ATTACHMENT_KINDS.filter((item) =>
          (draft.attachments || []).some((file) => file.kind === item.value),
        ).map((item) => (
          <li key={item.value}>{item.label}</li>
        ))}
      </ul>
      <p className="si-print-blanks">
        النماذج الرسمية الفارغة: {BLANK_FORMS.map((item) => item.label).join(" · ")}
      </p>
    </div>
  );
}
